(() => {
  'use strict';

  const purchaseConfirmationSelectors = Object.freeze({
    root: '[data-yushakobo-confirmation]',
    input: '[data-yushakobo-confirmation-input]',
    paymentContainer: '[data-yushakobo-payment-container]'
  });

  const tabSelectors = Object.freeze({
    root: '[data-yushakobo-tabs]',
    tab: '[data-yushakobo-tab]',
    panel: '[data-yushakobo-tab-panel]'
  });

  const gallerySelectors = Object.freeze({
    root: '[data-yushakobo-gallery]',
    scope: '[data-yushakobo-gallery-root]',
    trigger: '[data-yushakobo-gallery-trigger]',
    surface: '[data-yushakobo-gallery-surface]',
    stage: '[data-yushakobo-gallery-stage]',
    image: '[data-yushakobo-gallery-image]',
    display: '[data-yushakobo-gallery-display]',
    pending: '[data-yushakobo-gallery-pending]',
    close: '[data-yushakobo-gallery-close]',
    previous: '[data-yushakobo-gallery-previous]',
    next: '[data-yushakobo-gallery-next]',
    status: '[data-yushakobo-gallery-status]'
  });

  const galleryOpeners = new WeakMap();
  const galleryStates = new WeakMap();
  const preloadCache = new Map();
  const galleryFallbackClass = 'yushakobo-fullscreen-gallery-open';
  const galleryTriggerImage = '[data-yushakobo-gallery-trigger-image]';
  const transitionDuration = 120;
  const swipeThreshold = 48;
  const swipeDominance = 1.25;
  const swipeClickSuppression = 400;
  const maximumGalleryZoom = 4;

  const copyProductUrl = async (button) => {
    const root = button.closest('[data-yushakobo-share]');
    if (!root || button.disabled) return;
    const url = button.getAttribute('data-yushakobo-copy-url');
    const status = root.querySelector('[data-yushakobo-copy-status]');
    const field = root.querySelector('[data-yushakobo-copy-field]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    let copied = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(url);
          copied = true;
        } catch (_) { /* Fall back to selection-based copying when permission is unavailable. */ }
      }
      if (!copied && field) {
        field.hidden = false;
        field.focus();
        field.select();
        field.setSelectionRange(0, field.value.length);
        try { copied = document.execCommand('copy'); } catch (_) { copied = false; }
      }
      if (status) {
        status.classList.toggle('visually-hidden', copied);
        status.textContent = button.getAttribute(copied ? 'data-copy-success' : 'data-copy-error');
      }
      if (field) field.hidden = copied;
      if (copied) {
        const icon = button.querySelector('svg');
        if (icon && typeof icon.animate === 'function') {
          icon.getAnimations().forEach(animation => animation.cancel());
          const reducedMotion = typeof matchMedia === 'function'
            && matchMedia('(prefers-reduced-motion: reduce)').matches;
          icon.animate(reducedMotion
            ? [{ opacity: 1 }, { opacity: 1 }]
            : [{ opacity: 0.35 }, { opacity: 1 }, { opacity: 0.35 }, { opacity: 1 }, { opacity: 0.35 }],
          { duration: 800, easing: 'ease-in-out' });
        }
      }
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (copied) button.focus();
    }
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-yushakobo-copy-url]');
    if (button) {
      event.preventDefault();
      copyProductUrl(button);
    }
  });

  const getGalleryImages = (gallery) => Array.from(gallery.querySelectorAll(gallerySelectors.image));

  const normalizeGalleryIndex = (requestedIndex, imageCount) => (
    ((requestedIndex % imageCount) + imageCount) % imageCount
  );

  const getGalleryState = (gallery) => {
    let state = galleryStates.get(gallery);
    if (!state) {
      const initialIndex = Number.parseInt(gallery.getAttribute('data-yushakobo-gallery-current'), 10) || 0;
      state = {
        displayedIndex: initialIndex,
        requestedIndex: initialIndex,
        requestRevision: 0,
        transitionTimer: null,
        pointers: new Map(),
        gesture: null,
        zoom: { scale: 1, x: 0, y: 0 },
        suppressCloseUntil: 0,
        open: false
      };
      galleryStates.set(gallery, state);
    }
    return state;
  };

  const applyGalleryZoom = (gallery) => {
    const { zoom } = getGalleryState(gallery);
    const stage = gallery.querySelector(gallerySelectors.stage);
    const display = gallery.querySelector(gallerySelectors.display);
    if (!stage || !display) return;

    // Use untransformed dimensions so repeated gestures do not accumulate error.
    const limitX = Math.max(0, (display.offsetWidth * zoom.scale - stage.clientWidth) / 2);
    const limitY = Math.max(0, (display.offsetHeight * zoom.scale - stage.clientHeight) / 2);
    zoom.x = Math.max(-limitX, Math.min(limitX, zoom.x));
    zoom.y = Math.max(-limitY, Math.min(limitY, zoom.y));
    const transform = `translate3d(${zoom.x}px, ${zoom.y}px, 0) scale(${zoom.scale})`;
    [display, gallery.querySelector(gallerySelectors.pending)].forEach((image) => {
      if (image) image.style.transform = transform;
    });
    gallery.classList.toggle('is-zoomed', zoom.scale > 1);
  };

  const clearGalleryPointers = (gallery) => {
    const state = getGalleryState(gallery);
    const pointers = Array.from(state.pointers.entries());
    state.pointers.clear();
    state.gesture = null;
    pointers.forEach(([id, pointer]) => {
      if (pointer.target.hasPointerCapture?.(id)) pointer.target.releasePointerCapture(id);
    });
  };

  const resetGalleryZoom = (gallery) => {
    clearGalleryPointers(gallery);
    getGalleryState(gallery).zoom = { scale: 1, x: 0, y: 0 };
    applyGalleryZoom(gallery);
  };

  const startGalleryGesture = (gallery, allowSwipe = false) => {
    const state = getGalleryState(gallery);
    const points = Array.from(state.pointers.values());
    const { zoom } = state;
    if (points.length >= 2) {
      const bounds = gallery.querySelector(gallerySelectors.stage).getBoundingClientRect();
      const middleX = (points[0].x + points[1].x) / 2 - bounds.left - bounds.width / 2;
      const middleY = (points[0].y + points[1].y) / 2 - bounds.top - bounds.height / 2;
      state.gesture = {
        type: 'pinch',
        distance: Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y)),
        scale: zoom.scale,
        anchorX: (middleX - zoom.x) / zoom.scale,
        anchorY: (middleY - zoom.y) / zoom.scale
      };
      state.suppressCloseUntil = Date.now() + swipeClickSuppression;
    } else if (points.length === 1) {
      state.gesture = {
        type: allowSwipe && zoom.scale === 1 ? 'swipe' : 'pan',
        startX: points[0].x,
        startY: points[0].y,
        x: zoom.x,
        y: zoom.y
      };
    } else {
      state.gesture = null;
    }
  };

  const findNestedImage = (trigger) => {
    const taggedImage = trigger.querySelector(galleryTriggerImage);
    if (taggedImage instanceof HTMLImageElement) {
      return taggedImage;
    }

    const descendants = Array.from(trigger.children);
    while (descendants.length > 0) {
      const candidate = descendants.shift();
      if (candidate instanceof HTMLImageElement) {
        return candidate;
      }
      descendants.push(...Array.from(candidate.children));
    }
    return null;
  };

  const preloadGalleryImage = (source) => {
    if (preloadCache.has(source)) {
      return preloadCache.get(source);
    }

    const promise = new Promise((resolve, reject) => {
      const preload = new Image();
      preload.onload = async () => {
        try {
          if (typeof preload.decode === 'function') {
            await preload.decode();
          }
          resolve(source);
        } catch (error) {
          reject(error);
        }
      };
      preload.onerror = () => reject(new Error(`Unable to load gallery image: ${source}`));
      preload.src = source;
    });

    preloadCache.set(source, promise);
    promise.catch(() => {
      if (preloadCache.get(source) === promise) {
        preloadCache.delete(source);
      }
    });
    return promise;
  };

  const preloadGalleryNeighborhood = (images, requestedIndex) => {
    if (images.length === 0) {
      return;
    }

    const currentIndex = normalizeGalleryIndex(requestedIndex, images.length);
    const neighborIndexes = [currentIndex, currentIndex - 1, currentIndex + 1];
    new Set(neighborIndexes.map((index) => normalizeGalleryIndex(index, images.length)))
      .forEach((index) => {
        const source = images[index].getAttribute('data-yushakobo-gallery-src');
        if (source) {
          preloadGalleryImage(source).catch(() => {});
        }
      });
  };

  const updateGalleryPosition = (gallery, currentIndex, imageCount) => {
    gallery.setAttribute('data-yushakobo-gallery-current', String(currentIndex));
    const status = gallery.querySelector(gallerySelectors.status);
    if (status) {
      status.textContent = `${currentIndex + 1} / ${imageCount}`;
    }
  };

  const clearGalleryTransition = (gallery, state) => {
    if (state.transitionTimer !== null) {
      clearTimeout(state.transitionTimer);
      state.transitionTimer = null;
    }
    gallery.classList.remove('is-transitioning');
    gallery.classList.remove('is-committing');
    const pending = gallery.querySelector(gallerySelectors.pending);
    if (pending) {
      pending.hidden = true;
      pending.removeAttribute('src');
      pending.alt = '';
    }
  };

  const setGalleryBusy = (gallery, busy) => {
    gallery.classList.toggle('is-loading', busy);
    gallery.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  const restoreDisplayedGalleryState = (gallery, state, revision) => {
    if (revision !== state.requestRevision) {
      return;
    }
    state.requestedIndex = state.displayedIndex;
    clearGalleryTransition(gallery, state);
    setGalleryBusy(gallery, false);
    updateGalleryPosition(gallery, state.displayedIndex, getGalleryImages(gallery).length);
  };

  const commitGalleryImage = (gallery, image, currentIndex, revision) => {
    const state = getGalleryState(gallery);
    if (revision !== state.requestRevision || !state.open) {
      return;
    }

    const display = gallery.querySelector(gallerySelectors.display);
    const pending = gallery.querySelector(gallerySelectors.pending);
    if (!display || !pending) {
      return;
    }

    if (currentIndex !== state.displayedIndex) resetGalleryZoom(gallery);
    gallery.classList.add('is-committing');
    display.src = pending.src;
    display.alt = image.getAttribute('data-yushakobo-gallery-alt') || '';
    display.setAttribute('width', image.getAttribute('data-yushakobo-gallery-width') || '');
    display.setAttribute('height', image.getAttribute('data-yushakobo-gallery-height') || '');
    gallery.classList.remove('is-transitioning');
    pending.hidden = true;
    pending.removeAttribute('src');
    pending.alt = '';
    void gallery.offsetWidth;
    gallery.classList.remove('is-committing');
    setGalleryBusy(gallery, false);
    state.transitionTimer = null;
    state.displayedIndex = currentIndex;
    updateGalleryPosition(gallery, currentIndex, getGalleryImages(gallery).length);
    applyGalleryZoom(gallery);
  };

  const requestGalleryImage = async (gallery, requestedIndex) => {
    const images = getGalleryImages(gallery);
    if (images.length === 0) {
      return;
    }

    const state = getGalleryState(gallery);
    const currentIndex = normalizeGalleryIndex(requestedIndex, images.length);
    if (currentIndex !== state.displayedIndex) resetGalleryZoom(gallery);
    state.requestedIndex = currentIndex;
    state.requestRevision += 1;
    const revision = state.requestRevision;
    clearGalleryTransition(gallery, state);
    setGalleryBusy(gallery, true);
    preloadGalleryNeighborhood(images, currentIndex);

    const image = images[currentIndex];
    const source = image.getAttribute('data-yushakobo-gallery-src');
    if (!source) {
      restoreDisplayedGalleryState(gallery, state, revision);
      return;
    }

    try {
      await preloadGalleryImage(source);
    } catch (error) {
      restoreDisplayedGalleryState(gallery, state, revision);
      return;
    }

    if (revision !== state.requestRevision || !state.open) {
      return;
    }

    const pending = gallery.querySelector(gallerySelectors.pending);
    if (!pending) {
      restoreDisplayedGalleryState(gallery, state, revision);
      return;
    }
    pending.src = source;
    pending.setAttribute('width', image.getAttribute('data-yushakobo-gallery-width') || '');
    pending.setAttribute('height', image.getAttribute('data-yushakobo-gallery-height') || '');
    pending.hidden = false;
    applyGalleryZoom(gallery);

    const reducedMotion = typeof matchMedia === 'function'
      && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      commitGalleryImage(gallery, image, currentIndex, revision);
      return;
    }

    gallery.classList.add('is-transitioning');
    state.transitionTimer = setTimeout(() => {
      commitGalleryImage(gallery, image, currentIndex, revision);
    }, transitionDuration);
  };

  const showGalleryImage = (gallery, requestedIndex, previewSource = '') => {
    const images = getGalleryImages(gallery);
    if (images.length === 0) {
      return;
    }

    const currentIndex = normalizeGalleryIndex(requestedIndex, images.length);
    const state = getGalleryState(gallery);
    state.displayedIndex = currentIndex;
    state.requestedIndex = currentIndex;
    const display = gallery.querySelector(gallerySelectors.display);
    if (display && previewSource) {
      display.src = previewSource;
      display.alt = images[currentIndex].getAttribute('data-yushakobo-gallery-alt') || '';
    }
    updateGalleryPosition(gallery, currentIndex, images.length);
    requestGalleryImage(gallery, currentIndex);
  };

  const closeGallery = (gallery) => {
    const state = getGalleryState(gallery);
    state.open = false;
    state.requestRevision += 1;
    resetGalleryZoom(gallery);
    clearGalleryTransition(gallery, state);
    setGalleryBusy(gallery, false);

    if (typeof gallery.close === 'function' && gallery.open) {
      gallery.close();
    }

    gallery.hidden = true;
    gallery.removeAttribute('open');
    gallery.classList.remove('is-fallback-open');
    document.documentElement.classList.remove(galleryFallbackClass);

    const opener = galleryOpeners.get(gallery);
    if (opener && opener.isConnected) {
      opener.focus();
    }
    galleryOpeners.delete(gallery);
  };

  const openGallery = (trigger) => {
    const scope = trigger.closest(gallerySelectors.scope);
    const gallery = scope ? scope.querySelector(gallerySelectors.root) : null;
    if (!gallery) {
      return;
    }

    const requestedIndex = Number.parseInt(trigger.getAttribute('data-yushakobo-gallery-trigger'), 10) || 0;
    const triggerImage = findNestedImage(trigger);
    const previewSource = triggerImage ? (triggerImage.currentSrc || triggerImage.src) : '';
    const state = getGalleryState(gallery);
    state.open = true;
    resetGalleryZoom(gallery);
    state.suppressCloseUntil = 0;
    galleryOpeners.set(gallery, trigger);
    showGalleryImage(gallery, requestedIndex, previewSource);
    gallery.hidden = false;

    if (typeof gallery.showModal === 'function') {
      if (!gallery.open) {
        gallery.showModal();
      }
    } else {
      gallery.setAttribute('open', '');
      gallery.classList.add('is-fallback-open');
      document.documentElement.classList.add(galleryFallbackClass);
    }

    const closeButton = gallery.querySelector(gallerySelectors.close);
    if (closeButton) {
      closeButton.focus();
    }
  };

  const moveGallery = (gallery, offset) => {
    const images = getGalleryImages(gallery);
    if (images.length <= 1) {
      return;
    }
    const state = getGalleryState(gallery);
    requestGalleryImage(gallery, state.requestedIndex + offset);
  };

  const getOpenGallery = () => Array.from(document.querySelectorAll(gallerySelectors.root))
    .find((gallery) => !gallery.hidden);

  const getPointerGallery = (pointerId) => Array.from(document.querySelectorAll(gallerySelectors.root))
    .find((gallery) => {
      const state = galleryStates.get(gallery);
      return state && state.pointers.has(pointerId);
    });

  const updatePurchaseConfirmation = (root) => {
    const input = root.querySelector(purchaseConfirmationSelectors.input);
    const container = root.querySelector(purchaseConfirmationSelectors.paymentContainer);

    if (!input || !container) {
      return;
    }

    container.hidden = !input.checked;
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(purchaseConfirmationSelectors.root).forEach((root) => {
      updatePurchaseConfirmation(root);
    });
  });

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches(purchaseConfirmationSelectors.input)) {
      return;
    }

    const root = event.target.closest(purchaseConfirmationSelectors.root);
    if (root) {
      updatePurchaseConfirmation(root);
    }
  });

  const syncVopoVariantPrompt = (button) => {
    const form = button.closest('form');
    const appOptions = form?.querySelector('variant-picker #bcpo');
    if (!appOptions) {
      return;
    }

    const promptLabel = button.getAttribute('data-yushakobo-variant-prompt');
    const readyLabel = button.getAttribute('data-yushakobo-variant-ready');
    const currentLabel = button.textContent.trim();
    if (currentLabel !== promptLabel && currentLabel !== readyLabel) {
      return;
    }

    const hasVariant = Boolean(form.querySelector('input[name="id"]')?.value);
    const ready = hasVariant && !button.disabled && !appOptions.querySelector(':invalid');
    const nextLabel = ready ? readyLabel : promptLabel;
    if (currentLabel !== nextLabel) {
      button.textContent = nextLabel;
    }
  };

  const syncVopoVariantPrompts = () => {
    document.querySelectorAll('[data-yushakobo-variant-prompt]').forEach(syncVopoVariantPrompt);
  };

  document.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.closest('#bcpo')) {
      setTimeout(syncVopoVariantPrompts, 0);
    }
  }, true);

  document.addEventListener('DOMContentLoaded', () => {
    const productContent = document.querySelector('main');
    if (!productContent || !productContent.querySelector('[data-yushakobo-variant-prompt]')) {
      return;
    }

    new MutationObserver(syncVopoVariantPrompts).observe(productContent, {
      childList: true,
      subtree: true
    });
    syncVopoVariantPrompts();
  });

  const findTab = (target) => {
    if (!(target instanceof Element)) {
      return null;
    }

    return target.closest(tabSelectors.tab);
  };

  const activateTab = (tab, moveFocus = false) => {
    const root = tab.closest(tabSelectors.root);
    if (!root) {
      return;
    }

    const activeIndex = tab.getAttribute('data-yushakobo-tab');
    const tabs = root.querySelectorAll(tabSelectors.tab);
    const panels = root.querySelectorAll(tabSelectors.panel);

    tabs.forEach((candidate) => {
      const isActive = candidate.getAttribute('data-yushakobo-tab') === activeIndex;
      candidate.classList.toggle('is-active', isActive);
      candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
      candidate.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      const isActive = panel.getAttribute('data-yushakobo-tab-panel') === activeIndex;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    if (moveFocus) {
      tab.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const tab = findTab(event.target);
    if (tab) {
      activateTab(tab);
    }
  });

  document.addEventListener('keydown', (event) => {
    const tab = findTab(event.target);
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return;
    }

    const root = tab.closest(tabSelectors.root);
    const tabs = Array.from(root.querySelectorAll(tabSelectors.tab));
    const currentIndex = tabs.indexOf(tab);
    let nextIndex;

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
    }

    event.preventDefault();
    activateTab(tabs[nextIndex], true);
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const trigger = event.target.closest(gallerySelectors.trigger);
    if (trigger) {
      openGallery(trigger);
      return;
    }

    const gallery = event.target.closest(gallerySelectors.root);
    if (!gallery) {
      return;
    }

    if (event.target.closest(gallerySelectors.close)) {
      closeGallery(gallery);
      return;
    }

    if (event.target.closest(gallerySelectors.previous)) {
      moveGallery(gallery, -1);
      return;
    }

    if (event.target.closest(gallerySelectors.next)) {
      moveGallery(gallery, 1);
      return;
    }

    const renderedImage = event.target.closest(`${gallerySelectors.display}, ${gallerySelectors.pending}`);
    if (renderedImage) {
      const state = getGalleryState(gallery);
      if (state.pointers.size > 0 || state.zoom.scale > 1
        || Date.now() < state.suppressCloseUntil || getGalleryImages(gallery).length <= 1) {
        return;
      }

      const bounds = renderedImage.getBoundingClientRect();
      const clientX = event.clientX;
      if (!Number.isFinite(clientX)
        || !Number.isFinite(bounds.left)
        || !Number.isFinite(bounds.width)
        || bounds.width <= 0
        || clientX < bounds.left
        || clientX > bounds.left + bounds.width) {
        return;
      }

      const center = bounds.left + (bounds.width / 2);
      if (clientX < center) {
        moveGallery(gallery, -1);
      } else {
        moveGallery(gallery, 1);
      }
      return;
    }

    if (event.target instanceof HTMLImageElement) {
      return;
    }

    const surface = event.target.closest(gallerySelectors.surface);
    if (event.target === gallery || surface) {
      const state = getGalleryState(gallery);
      if (state.pointers.size > 0 || Date.now() < state.suppressCloseUntil
        || (state.zoom.scale > 1 && event.target.closest(gallerySelectors.stage))) {
        return;
      }
      closeGallery(gallery);
    }
  });

  document.addEventListener('pointerdown', (event) => {
    if (!(event.target instanceof Element) || event.button !== 0) return;
    const stage = event.target.closest(gallerySelectors.stage);
    const gallery = stage?.closest(gallerySelectors.root);
    if (!gallery || gallery.hidden) return;

    const state = getGalleryState(gallery);
    state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, target: event.target });
    // Capture on the original target so a stationary tap still targets the image.
    event.target.setPointerCapture?.(event.pointerId);
    startGalleryGesture(gallery, state.pointers.size === 1);
  });

  document.addEventListener('pointermove', (event) => {
    const gallery = getPointerGallery(event.pointerId);
    if (!gallery) return;
    if (event.cancelable) event.preventDefault();
    const state = getGalleryState(gallery);
    const point = state.pointers.get(event.pointerId);
    point.x = event.clientX;
    point.y = event.clientY;
    const gesture = state.gesture;
    if (!gesture) return;

    const points = Array.from(state.pointers.values());
    if (gesture.type === 'pinch' && points.length >= 2) {
      const bounds = gallery.querySelector(gallerySelectors.stage).getBoundingClientRect();
      const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
      state.zoom.scale = Math.max(1, Math.min(maximumGalleryZoom, gesture.scale * distance / gesture.distance));
      state.zoom.x = (points[0].x + points[1].x) / 2 - bounds.left - bounds.width / 2
        - gesture.anchorX * state.zoom.scale;
      state.zoom.y = (points[0].y + points[1].y) / 2 - bounds.top - bounds.height / 2
        - gesture.anchorY * state.zoom.scale;
      applyGalleryZoom(gallery);
    } else if (gesture.type === 'pan') {
      state.zoom.x = gesture.x + point.x - gesture.startX;
      state.zoom.y = gesture.y + point.y - gesture.startY;
      applyGalleryZoom(gallery);
    }

    if (gesture.type !== 'swipe'
      || Math.hypot(point.x - gesture.startX, point.y - gesture.startY) > 8) {
      state.suppressCloseUntil = Date.now() + swipeClickSuppression;
    }
  }, { passive: false });

  const finishGalleryPointer = (event) => {
    const gallery = getPointerGallery(event.pointerId);
    if (!gallery) return;
    const state = getGalleryState(gallery);
    const gesture = state.gesture;
    const point = state.pointers.get(event.pointerId);
    state.pointers.delete(event.pointerId);
    if (point.target.hasPointerCapture?.(event.pointerId)) {
      point.target.releasePointerCapture(event.pointerId);
    }

    // A remaining finger after a pinch can pan, but never switches the image.
    startGalleryGesture(gallery);
    if (state.zoom.scale < 1.02 && state.pointers.size === 0) {
      state.zoom = { scale: 1, x: 0, y: 0 };
      applyGalleryZoom(gallery);
    }
    if (!gesture) return;
    if (gesture.type !== 'swipe' || event.type !== 'pointerup') {
      state.suppressCloseUntil = Date.now() + swipeClickSuppression;
      return;
    }

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    if (Math.abs(deltaX) >= swipeThreshold && Math.abs(deltaX) >= Math.abs(deltaY) * swipeDominance) {
      state.suppressCloseUntil = Date.now() + swipeClickSuppression;
      moveGallery(gallery, deltaX < 0 ? 1 : -1);
    }
  };

  document.addEventListener('pointerup', finishGalleryPointer);
  document.addEventListener('pointercancel', finishGalleryPointer);
  document.addEventListener('lostpointercapture', finishGalleryPointer);
  window.addEventListener('resize', () => {
    const gallery = getOpenGallery();
    if (gallery) resetGalleryZoom(gallery);
  });

  document.addEventListener('keydown', (event) => {
    const gallery = getOpenGallery();
    if (!gallery || !['ArrowLeft', 'ArrowRight', 'Escape'].includes(event.key)) {
      return;
    }

    event.preventDefault();
    if (event.key === 'Escape') {
      closeGallery(gallery);
    } else {
      moveGallery(gallery, event.key === 'ArrowLeft' ? -1 : 1);
    }
  });

  document.addEventListener('cancel', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches(gallerySelectors.root)) {
      return;
    }

    event.preventDefault();
    closeGallery(event.target);
  }, true);

  class ProductGallerySticky extends HTMLElement {
    connectedCallback() {
      this.updatePosition = () => {
        if (window.innerWidth < 1000) {
          this.style.removeProperty('--gallery-sticky-top');
          return;
        }
        const styles = getComputedStyle(document.documentElement);
        const headerHeight = parseFloat(styles.getPropertyValue('--header-height')) || 0;
        const stickyHeader = parseFloat(styles.getPropertyValue('--header-is-sticky')) || 0;
        const headerOffset = headerHeight * stickyHeader + 30;
        // A tall gallery scrolls far enough to keep its thumbnails accessible.
        const bottomOffset = window.innerHeight - this.getBoundingClientRect().height - 30;
        this.style.setProperty('--gallery-sticky-top', `${Math.min(headerOffset, bottomOffset)}px`);
      };
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(this.updatePosition);
        this.resizeObserver.observe(this);
        const header = document.querySelector('[data-section-type="header"]');
        if (header) this.resizeObserver.observe(header);
      }
      window.addEventListener('resize', this.updatePosition);
      this.updatePosition();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.updatePosition);
    }
  }

  if (!customElements.get('product-gallery-sticky')) {
    customElements.define('product-gallery-sticky', ProductGallerySticky);
  }

})();
